import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Layers, RefreshCw, Save, Calculator, AlertTriangle, Plus, Trash2,
  Link2, Check, Percent, ExternalLink, X, Lock, Eye, EyeOff, Archive, RotateCcw,
  Sparkles, Zap, Crown, Star, Compass, Rocket, Shield, Users,
  Gem, Flame, Trophy, Heart, CloudLightning, CreditCard,
} from 'lucide-react';
import { Spinner } from '@/components/shared/UIComponents';
import { ContextualHelp } from '@/components/help/ContextualHelp';
import { toast } from 'sonner';
import {
  fetchB2CPlansForBuilder, createB2CPlan, updateB2CPlan, toggleB2CPlan, deleteB2CPlan,
  syncB2CPlanStripe, setB2CStripeEnabled, createConnectOnboardingLink, fetchConnectStatus, fetchPlanFeatureDefaults,
} from '@/lib/api';
import { usePlatformStore } from '@/lib/store';
import { APP_FEATURE_KEYS, computeAllowedB2CFeatures, B2C_ICON_OPTIONS } from '@/lib/constants';
import { PricingAssistantPanel, type PricingChatContext, type ChatMessage } from '@/components/pricing-assistant/PricingAssistantPanel';
import type {
  Tenant, ConnectAccountStatus, AppTenantPricingPlan,
  B2CFeature, B2CPlanFeature, B2CPlanInput,
} from '@/types';

// ── Icon map ──────────────────────────────────────────────────────────────────

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  sparkles: Sparkles, zap: Zap, crown: Crown, star: Star,
  compass: Compass, rocket: Rocket, shield: Shield, users: Users,
  gem: Gem, flame: Flame, trophy: Trophy, heart: Heart,
};

// ── Color presets ─────────────────────────────────────────────────────────────

const COLOR_PRESETS = [
  '#6ECFF6', '#3ED6C3', '#2AB3B1', '#D97706', '#3b82f6',
  '#8b5cf6', '#ec4899', '#ef4444', '#22c55e', '#64748b',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function slugify(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40);
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface PlanFormState {
  id: string | null;            // null = new plan
  plan_key: string;
  name: string;
  description: string;
  tagline: string;
  accent_color: string;
  icon_key: string;
  is_popular: boolean;
  cta_label: string;
  monthly_price: string;
  annual_price: string;
  sort_order: string;
  tenant_share_pct: string;
  unravel_share_pct: string;
  is_default: boolean;
  is_active: boolean;
  // Per-feature state
  features: Record<string, {
    is_enabled: boolean;
    limit_value: string;
    limit_period: string;
    display_limit: string;
  }>;
}

type Tab = 'general' | 'pricing' | 'features';

// ── Component ─────────────────────────────────────────────────────────────────

export function B2CPlansPage({ tenant }: { tenant: Tenant }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectTenant } = usePlatformStore();

  // Data
  const [plans, setPlans] = useState<AppTenantPricingPlan[]>([]);
  const [features, setFeatures] = useState<B2CFeature[]>([]);
  const [planFeatures, setPlanFeatures] = useState<B2CPlanFeature[]>([]);
  const [subscriberCounts, setSubscriberCounts] = useState<Record<string, number>>({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('general');
  const [dirty, setDirty] = useState(false);
  const [formState, setFormState] = useState<PlanFormState | null>(null);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [syncingPlanId, setSyncingPlanId] = useState<string | null>(null);

  // Stripe payments enabled flag (controls "Upgrade Now" vs "Coming Soon" in the B2C app)
  const [stripePaymentsEnabled, setStripePaymentsEnabled] = useState(false);
  const [togglingStripe, setTogglingStripe] = useState(false);

  // Stripe Connect state
  const [connectStatus, setConnectStatus] = useState<ConnectAccountStatus | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [connectReturnSuccess, setConnectReturnSuccess] = useState(false);

  // Revenue preview
  const [previewUsers, setPreviewUsers] = useState<Record<string, string>>({});

  // ── B2B ceiling — computed from plan_feature_defaults ──────────────────
  const [allowedB2CKeys, setAllowedB2CKeys] = useState<Set<string>>(new Set());

  // ── Pricing Assistant (B2C variant) ─────────────────────────────────
  // Modal chat scoped to the currently-selected plan. Anchors on an
  // anonymous internal benchmark across other DiscoveryGem partners
  // (never names them, only aggregate counts + averages) instead of
  // external travel-SaaS competitors — competitor anchors here would
  // risk losing customers if this tenant's price comes out above
  // market. See supabase/functions/pricing-assistant-b2c for the
  // edge function + system prompt.
  //
  // Conversations are keyed by plan.id so closing + reopening the
  // modal, or switching plans and coming back, restores each plan's
  // prior chat thread.
  const [assistantOpen, setAssistantOpen]       = useState(false);
  const [assistantContext, setAssistantContext] = useState<PricingChatContext | null>(null);
  const [conversations, setConversations]       = useState<Record<string, ChatMessage[]>>({});
  // Ceiling limits: feature_key → max limit_value from the platform plan
  const [ceilingLimits, setCeilingLimits] = useState<Record<string, number | null>>({});
  const appKeySet = useMemo(() => new Set(APP_FEATURE_KEYS), []);

  useEffect(() => {
    if (!tenant.plan_id) return;
    fetchPlanFeatureDefaults(tenant.plan_id).then(rows => {
      const allowed = new Set<string>();
      const limits: Record<string, number | null> = {};
      for (const r of rows) {
        if (r.is_enabled && appKeySet.has(r.feature_key)) {
          allowed.add(r.feature_key);
          limits[r.feature_key] = r.limit_value; // null = unlimited
        }
      }
      // Fallback for old plans without B2C feature defaults
      if (allowed.size === 0 && tenant.features) {
        computeAllowedB2CFeatures(tenant.features).forEach(k => allowed.add(k));
      }
      setAllowedB2CKeys(allowed);
      setCeilingLimits(limits);
    });
  }, [tenant.plan_id, tenant.features, appKeySet]);

  // ── B2C plan count limits ──────────────────────────────────────────────
  const maxTotal = tenant.plan?.max_b2c_plans ?? 4;
  const maxFree  = tenant.plan?.max_b2c_free_plans ?? 1;

  // Count ALL plans (active + archived + deleted) to prevent gaming via archive/delete
  const totalPlanCount = plans.length;
  const freePlanCount  = useMemo(
    () => plans.filter(p => Number(p.monthly_price) === 0).length,
    [plans],
  );

  const atTotalLimit = maxTotal !== -1 && totalPlanCount >= maxTotal;
  const atFreeLimit  = maxFree  !== -1 && freePlanCount  >= maxFree;
  const canAddPlan   = !atTotalLimit;

  // ── Data loading ────────────────────────────────────────────────────────

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await fetchB2CPlansForBuilder(tenant.id);
      setPlans(data.plans);
      setFeatures(data.features);
      setPlanFeatures(data.planFeatures);
      setSubscriberCounts(data.subscriberCounts);
      if (typeof data.stripeEnabled === 'boolean') {
        setStripePaymentsEnabled(data.stripeEnabled);
      }

      // Auto-select first plan if none selected
      if (!selectedPlanId && data.plans.length > 0) {
        const firstActive = data.plans.find(p => p.is_active) || data.plans[0];
        setSelectedPlanId(firstActive.id);
        buildFormState(firstActive, data.features, data.planFeatures);
      } else if (selectedPlanId) {
        const plan = data.plans.find(p => p.id === selectedPlanId);
        if (plan) buildFormState(plan, data.features, data.planFeatures);
      }

      setDirty(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenant.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  // ── Stripe Connect ──────────────────────────────────────────────────────

  useEffect(() => {
    const checkConnect = async () => {
      if (!tenant.stripe_connect_id) return;
      try {
        const status = await fetchConnectStatus(tenant.id);
        setConnectStatus(status);
      } catch { /* silent */ }
    };
    checkConnect();
  }, [tenant.id, tenant.stripe_connect_id]);

  useEffect(() => {
    if (searchParams.get('connect_return') === 'true') {
      setConnectReturnSuccess(true);
      selectTenant(tenant.id);
      searchParams.delete('connect_return');
      setSearchParams(searchParams, { replace: true });
      const recheck = async () => {
        try {
          const status = await fetchConnectStatus(tenant.id);
          setConnectStatus(status);
        } catch { /* ignore */ }
      };
      recheck();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConnectOnboarding = async () => {
    setConnectLoading(true);
    setConnectError(null);
    try {
      const returnUrl = `${window.location.origin}${window.location.pathname}?connect_return=true`;
      const result = await createConnectOnboardingLink(tenant.id, returnUrl, returnUrl);
      if (result.auto_onboarded) {
        await selectTenant(tenant.id);
        try {
          const status = await fetchConnectStatus(tenant.id);
          setConnectStatus(status);
        } catch {
          setConnectStatus({
            account_id: result.account_id,
            charges_enabled: true, payouts_enabled: true,
            details_submitted: true, fully_onboarded: true,
          });
        }
        setConnectReturnSuccess(true);
        setConnectLoading(false);
      } else if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error('No onboarding URL returned');
      }
    } catch (err) {
      setConnectError((err as Error).message);
      setConnectLoading(false);
    }
  };

  // ── Form state management ───────────────────────────────────────────────

  function buildFormState(plan: AppTenantPricingPlan, feats: B2CFeature[], pfs: B2CPlanFeature[]) {
    const featureState: PlanFormState['features'] = {};
    for (const f of feats) {
      const pf = pfs.find(p => p.plan_id === plan.id && p.feature_id === f.id);
      featureState[f.id] = {
        is_enabled: pf?.is_enabled ?? false,
        limit_value: pf?.limit_value != null ? String(pf.limit_value) : '',
        limit_period: pf?.limit_period ?? '',
        display_limit: pf?.display_limit ?? '',
      };
    }

    setFormState({
      id: plan.id,
      plan_key: plan.plan_key,
      name: plan.name,
      description: plan.description || '',
      tagline: plan.tagline || '',
      accent_color: plan.accent_color || '#64748b',
      icon_key: plan.icon_key || 'sparkles',
      is_popular: plan.is_popular ?? false,
      cta_label: plan.cta_label || 'Start Free Trial',
      monthly_price: String(plan.monthly_price),
      annual_price: String(plan.annual_price),
      sort_order: String(plan.sort_order),
      tenant_share_pct: String(plan.tenant_share_pct),
      unravel_share_pct: String(plan.unravel_share_pct),
      is_default: plan.is_default,
      is_active: plan.is_active,
      features: featureState,
    });
    setIsNewPlan(false);
  }

  function handleNewPlan() {
    if (!canAddPlan) return;
    const featureState: PlanFormState['features'] = {};
    for (const f of features) {
      featureState[f.id] = { is_enabled: false, limit_value: '', limit_period: '', display_limit: '' };
    }
    setFormState({
      id: null,
      plan_key: '',
      name: '',
      description: '',
      tagline: '',
      accent_color: '#64748b',
      icon_key: 'sparkles',
      is_popular: false,
      cta_label: 'Start Free Trial',
      monthly_price: '4.99',
      annual_price: '49',
      sort_order: String((plans.length + 1) * 10),
      tenant_share_pct: '70',
      unravel_share_pct: '30',
      is_default: false,
      is_active: true,
      features: featureState,
    });
    setSelectedPlanId(null);
    setIsNewPlan(true);
    setActiveTab('general');
    setDirty(true);
  }

  function updateForm(field: keyof PlanFormState, value: unknown) {
    if (!formState) return;
    if (field === 'name' && isNewPlan) {
      setFormState(prev => prev ? ({ ...prev, [field]: value, plan_key: slugify(value as string) } as PlanFormState) : prev);
    } else {
      setFormState(prev => prev ? { ...prev, [field]: value } : prev);
    }
    setDirty(true);
    setSaveSuccess(false);
    setSaveError(null);
  }

  function updateFeature(featureId: string, field: string, value: unknown) {
    if (!formState) return;
    setFormState({
      ...formState,
      features: {
        ...formState.features,
        [featureId]: {
          ...formState.features[featureId],
          [field]: value,
        },
      },
    });
    setDirty(true);
    setSaveSuccess(false);
  }

  // ── Pricing Assistant — open / apply / thread mgmt ─────────────────
  const openAssistant = useCallback(() => {
    setAssistantOpen(true);
    if (!formState || !selectedPlanId) {
      setAssistantContext(null);
      return;
    }

    // Map enabled feature ids → human labels from the loaded catalog
    // so the agent's kickoff reads "Smart Packing", "AI Chatbots" etc.
    // instead of UUIDs.
    const featureLabels = Object.entries(formState.features)
      .filter(([, f]) => f.is_enabled)
      .map(([fid]) => features.find(f => f.id === fid)?.name)
      .filter((label): label is string => !!label)
      .sort();

    const monthly = parseFloat(formState.monthly_price) || 0;
    const annual  = parseFloat(formState.annual_price)  || 0;

    setAssistantContext({
      id:           selectedPlanId,
      display_name: formState.name || 'this plan',
      kickoff:      buildB2CKickoff({
        display_name:   formState.name || 'this plan',
        monthly_usd:    monthly,
        annual_usd:     annual,
        feature_labels: featureLabels,
      }),
    });
  }, [formState, selectedPlanId, features]);

  const handleAssistantApply = useCallback(
    (monthlyCents: number | null, annualCents: number | null) => {
      // formState stores price as a STRING (not cents). Convert cents
      // → dollars string to drop straight into the input. null = leave
      // the existing field alone (Custom pricing case).
      if (!formState) return;
      const monthlyStr = monthlyCents !== null ? (monthlyCents / 100).toFixed(2).replace(/\.00$/, '') : formState.monthly_price;
      const annualStr  = annualCents  !== null ? (annualCents  / 100).toFixed(2).replace(/\.00$/, '') : formState.annual_price;
      setFormState(prev => prev ? {
        ...prev,
        monthly_price: monthlyStr,
        annual_price:  annualStr,
      } : prev);
      setDirty(true);
      setSaveSuccess(false);
    },
    [formState],
  );

  const handleConversationChange = useCallback(
    (next: ChatMessage[]) => {
      if (!assistantContext) return;
      setConversations(prev => ({ ...prev, [assistantContext.id]: next }));
    },
    [assistantContext],
  );

  const handleConversationReset = useCallback(() => {
    if (!assistantContext) return;
    setConversations(prev => {
      const next = { ...prev };
      delete next[assistantContext.id];
      return next;
    });
  }, [assistantContext]);

  // ── Save ────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formState) return;

    // Validate
    const monthlyPrice = parseFloat(formState.monthly_price) || 0;
    const isPaidPlan = monthlyPrice > 0;
    if (isPaidPlan) {
      const annual = parseFloat(formState.annual_price);
      const unravelPct = parseFloat(formState.unravel_share_pct);
      if (monthlyPrice < 0.99) { setSaveError('Monthly price must be at least $0.99'); return; }
      if (isNaN(annual) || annual < 0.99) { setSaveError('Annual price must be at least $0.99'); return; }
      if (isNaN(unravelPct) || unravelPct < 20) { setSaveError('DiscoveryGem share must be at least 20%'); return; }
    }
    if (!formState.name.trim()) { setSaveError('Plan name is required'); return; }
    if (!formState.plan_key.trim()) { setSaveError('Plan key is required'); return; }

    // Plan count gating
    if (isNewPlan) {
      if (atTotalLimit) { setSaveError(`Plan limit reached (${totalPlanCount}/${maxTotal}). Upgrade your B2B plan to add more.`); return; }
      const monthlyVal = parseFloat(formState.monthly_price) || 0;
      if (monthlyVal === 0 && atFreeLimit) {
        setSaveError(`Free plan limit reached (${freePlanCount}/${maxFree}). Set a price above $0 or upgrade your B2B plan.`);
        return;
      }
    }

    setSaving(true);
    setSaveError(null);
    try {
      // Build featureId → key lookup for ceiling clamping
      const idToKey: Record<string, string> = {};
      for (const feat of features) idToKey[feat.id] = feat.key;

      const featureArray = Object.entries(formState.features).map(([featureId, f]) => {
        let limitVal = f.limit_value ? parseInt(f.limit_value) : null;
        // Clamp to platform plan ceiling
        const key = idToKey[featureId];
        if (key && limitVal != null) {
          const ceil = ceilingLimits[key];
          if (ceil != null && ceil > 0 && limitVal > ceil) limitVal = ceil;
        }
        return {
          feature_id: featureId,
          is_enabled: f.is_enabled,
          limit_value: limitVal,
          limit_period: f.limit_period || null,
          display_limit: f.display_limit || '',
        };
      });

      // Always compute shares so they sum to exactly 100
      const unravelPct = parseFloat(formState.unravel_share_pct) || 30;
      const tenantPct = 100 - unravelPct;

      const planPayload = {
        name: formState.name,
        description: formState.description,
        tagline: formState.tagline,
        accent_color: formState.accent_color,
        icon_key: formState.icon_key,
        is_popular: formState.is_popular,
        cta_label: formState.cta_label,
        monthly_price: parseFloat(formState.monthly_price) || 0,
        annual_price: parseFloat(formState.annual_price) || 0,
        sort_order: parseInt(formState.sort_order) || 50,
        tenant_share_pct: tenantPct,
        unravel_share_pct: unravelPct,
        features: featureArray,
      };

      if (isNewPlan) {
        await createB2CPlan(tenant.id, { plan_key: formState.plan_key, ...planPayload } as B2CPlanInput, undefined, { max_b2c_plans: maxTotal, max_b2c_free_plans: maxFree }, tenant.name);
      } else if (formState.id) {
        await updateB2CPlan(tenant.id, formState.id, planPayload as Partial<B2CPlanInput>, undefined, tenant.name);
      }

      setIsNewPlan(false);
      await loadData(true);          // silent refresh — no full-screen spinner
      setSaveSuccess(true);
      setDirty(false);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle / Delete ─────────────────────────────────────────────────────

  const handleToggle = async (planId: string, isActive: boolean) => {
    try {
      await toggleB2CPlan(tenant.id, planId, isActive, undefined, tenant.name);
      await loadData(true);           // silent refresh
    } catch (err) {
      setSaveError((err as Error).message);
    }
  };

  const handleSyncStripe = async (planId: string) => {
    setSyncingPlanId(planId);
    setSaveError(null);
    try {
      await syncB2CPlanStripe(tenant.id, planId, tenant.name);
      await loadData(true);
      setSaveSuccess(true);
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSyncingPlanId(null);
    }
  };

  const handleDelete = async (planId: string) => {
    try {
      await deleteB2CPlan(tenant.id, planId);
      setDeleteConfirm(null);
      if (selectedPlanId === planId) {
        setSelectedPlanId(null);
        setFormState(null);
      }
      await loadData(true);           // silent refresh
    } catch (err) {
      setSaveError((err as Error).message);
    }
  };

  // ── Revenue preview (blends unsaved formState edits for selected plan) ──

  const revenue = useMemo(() => {
    let tenantMonthly = 0;
    let unravelMonthly = 0;
    for (const plan of plans) {
      // If the user is editing this plan, use formState values so preview is live
      const isEditing = formState && plan.plan_key === formState.plan_key;
      const price = isEditing ? (parseFloat(formState.monthly_price) || 0) : plan.monthly_price;
      const tPct = isEditing ? (100 - (parseFloat(formState.unravel_share_pct) || 30)) : plan.tenant_share_pct;
      const uPct = isEditing ? (parseFloat(formState.unravel_share_pct) || 30) : plan.unravel_share_pct;
      if (price === 0) continue;
      const users = parseInt(previewUsers[plan.plan_key] || '0') || 0;
      const gross = price * users;
      tenantMonthly += gross * (tPct / 100);
      unravelMonthly += gross * (uPct / 100);
    }
    // If it's a new plan not yet in `plans`, include it too
    if (formState && isNewPlan) {
      const price = parseFloat(formState.monthly_price) || 0;
      const uPct = parseFloat(formState.unravel_share_pct) || 30;
      const tPct = 100 - uPct;
      if (price > 0) {
        const users = parseInt(previewUsers[formState.plan_key] || '0') || 0;
        const gross = price * users;
        tenantMonthly += gross * (tPct / 100);
        unravelMonthly += gross * (uPct / 100);
      }
    }
    return { tenantMonthly, unravelMonthly, total: tenantMonthly + unravelMonthly };
  }, [plans, previewUsers, formState, isNewPlan]);

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Spinner size="lg" /></div>;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div><h1 className="text-xl font-bold text-surface-900">B2C Plan Tiers</h1></div>
        <div className="p-6 rounded-xl banner-red border text-center">
          <AlertTriangle size={24} className="mx-auto text-red-600 dark:text-red-400 mb-2" />
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          <button onClick={() => loadData()} className="btn-secondary text-sm mt-3"><RefreshCw size={14} /> Retry</button>
        </div>
      </div>
    );
  }

  const selectedPlan = selectedPlanId ? plans.find(p => p.id === selectedPlanId) : null;
  const PlanIcon = formState ? ICON_MAP[formState.icon_key] || Sparkles : Sparkles;

  // Stripe sync status helper
  const getStripeSyncStatus = (plan: AppTenantPricingPlan) => {
    const isPaid = Number(plan.monthly_price) > 0;
    if (!isPaid) return 'free'; // free plan — no Stripe needed
    if (plan.stripe_product_id && plan.stripe_monthly_price_id && plan.stripe_annual_price_id) return 'synced';
    return 'needs_sync';
  };

  return (
    <div className="space-y-6">
      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-surface-900">B2C Plan Tiers</h1>
            <span className="text-xs text-surface-400">
              {maxTotal === -1 ? `${totalPlanCount} plans` : `${totalPlanCount}/${maxTotal} plans`}
              {maxFree !== -1 && ` · ${freePlanCount}/${maxFree} free`}
            </span>
          </div>
          <p className="text-sm text-surface-500 mt-1">
            Configure the plans your end users see on the pricing page
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Pricing Assistant — modal chat that suggests pricing for
              the currently-selected plan, anchored on an anonymous
              benchmark across other DiscoveryGem partners (never
              names them, never shows external travel-SaaS competitor
              prices). Disabled state happens via the modal itself:
              if no plan is selected, the modal shows an empty-state
              prompt. */}
          <button
            onClick={openAssistant}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-brand-500/40 text-brand-600 dark:text-brand-400 bg-brand-500/[0.06] hover:bg-brand-500/[0.12] transition-all"
            title="AI pricing suggestions grounded in an anonymous benchmark across DiscoveryGem partners"
          >
            <Sparkles size={13} /> Pricing Assistant
          </button>
          <button onClick={() => loadData()} className="btn-secondary text-sm" title="Refresh"><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* ── Stripe Connect Status ─────────────────────────────────── */}
      <div className="glass-panel p-4">
        {connectReturnSuccess && (
          <div className="mb-3 flex items-center gap-2 p-2 rounded-lg banner-emerald border text-sm text-emerald-600 dark:text-emerald-400">
            <Check size={14} /> Stripe Connect onboarding complete!
            <button onClick={() => setConnectReturnSuccess(false)} className="ml-auto p-0.5 rounded hover:bg-emerald-100 dark:bg-emerald-900/30"><X size={14} /></button>
          </div>
        )}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-surface-100"><Link2 size={16} className="text-surface-500" /></div>
          <div className="flex-1">
            {connectStatus?.fully_onboarded ? (
              <>
                <div className="text-sm font-medium text-surface-900 flex items-center gap-2">
                  Stripe Connect
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-emerald border border-emerald-800/40">
                    <Check size={10} /> Connected
                  </span>
                </div>
                <div className="text-xs text-surface-500 mt-0.5 flex items-center gap-3">
                  <span>Account: {connectStatus.account_id.slice(0, 16)}...</span>
                  <span className={connectStatus.charges_enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                    Charges {connectStatus.charges_enabled ? '✓' : 'pending'}
                  </span>
                  <span className={connectStatus.payouts_enabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                    Payouts {connectStatus.payouts_enabled ? '✓' : 'pending'}
                  </span>
                </div>
              </>
            ) : tenant.stripe_connect_id ? (
              <>
                <div className="text-sm font-medium text-surface-900 flex items-center gap-2">
                  Stripe Connect
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-amber border border-amber-800/40">
                    <AlertTriangle size={10} /> Pending
                  </span>
                </div>
                <div className="text-xs text-surface-500 mt-0.5">Onboarding incomplete. Complete setup to receive payouts.</div>
              </>
            ) : (
              <>
                <div className="text-sm font-medium text-surface-900">Stripe Connect — Not Connected</div>
                <div className="text-xs text-surface-500 mt-0.5">Connect a Stripe account to receive payouts.</div>
              </>
            )}
          </div>
          <div className="flex-shrink-0">
            {connectStatus?.fully_onboarded ? (
              <a href={`https://dashboard.stripe.com/connect/accounts/${connectStatus.account_id}`} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs flex items-center gap-1.5">
                <ExternalLink size={12} /> Stripe Dashboard
              </a>
            ) : (
              <button onClick={handleConnectOnboarding} disabled={connectLoading} className="btn-primary text-xs">
                {connectLoading ? <><RefreshCw size={12} className="animate-spin" /> Loading...</> :
                  tenant.stripe_connect_id ? <><ExternalLink size={12} /> Complete Onboarding</> :
                  <><Link2 size={12} /> Connect Stripe</>}
              </button>
            )}
          </div>
        </div>
        {connectError && (
          <div className="mt-2 flex items-center gap-2 text-xs text-red-600 dark:text-red-400"><AlertTriangle size={12} /> {connectError}</div>
        )}
      </div>

      {/* ── Stripe Payments Toggle ────────────────────────────────── */}
      <div className="glass-panel p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${stripePaymentsEnabled ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-surface-100'}`}>
            <CreditCard size={16} className={stripePaymentsEnabled ? 'text-emerald-600 dark:text-emerald-400' : 'text-surface-500'} />
          </div>
          <div>
            <div className="text-sm font-medium text-surface-900 flex items-center gap-2">
              <ContextualHelp helpKey="stripe-toggle">Stripe Payments</ContextualHelp>
              {stripePaymentsEnabled ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-emerald border border-emerald-800/40">
                  <Check size={10} /> Live
                </span>
              ) : (
                <ContextualHelp helpKey="coming-soon">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium badge-amber border border-amber-800/40">
                    Coming Soon
                  </span>
                </ContextualHelp>
              )}
            </div>
            <div className="text-xs text-surface-500 mt-0.5">
              {stripePaymentsEnabled
                ? 'Users can subscribe to paid plans via Stripe checkout.'
                : 'Upgrade button shows "Coming Soon" to end users. Turn on when ready to accept payments.'}
            </div>
          </div>
        </div>
        <button
          onClick={async () => {
            setTogglingStripe(true);
            try {
              const willEnable = !stripePaymentsEnabled;
              await setB2CStripeEnabled(tenant.id, willEnable);
              setStripePaymentsEnabled(willEnable);
              if (willEnable) {
                toast.success('Stripe Payments enabled! Next step: sync each paid plan to Stripe so users can check out.', { duration: 8000 });
              }
            } catch (err) {
              setSaveError((err as Error).message);
            } finally {
              setTogglingStripe(false);
            }
          }}
          disabled={togglingStripe}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${stripePaymentsEnabled ? 'bg-emerald-500' : 'bg-surface-300'} ${togglingStripe ? 'opacity-50' : ''}`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition duration-200 ease-in-out ${stripePaymentsEnabled ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>

      {/* ── Plan Cards Grid ───────────────────────────────────────── */}
      {(() => {
        const activePlans = plans.filter(p => p.is_active);
        const archivedPlans = plans.filter(p => !p.is_active);
        const archivedCount = archivedPlans.length;

        return (
          <>
            {/* Active plans */}
            <div className="flex gap-3 overflow-x-auto pb-2">
              {activePlans.map(plan => {
                const Icon = ICON_MAP[plan.icon_key || ''] || Sparkles;
                const isSelected = plan.id === selectedPlanId && !isNewPlan;
                const subs = subscriberCounts[plan.id] ?? 0;

                return (
                  <div key={plan.id} className="flex-shrink-0 w-[180px] relative group">
                    <button
                      onClick={() => {
                        if (dirty && !confirm('Discard unsaved changes?')) return;
                        setSelectedPlanId(plan.id);
                        buildFormState(plan, features, planFeatures);
                        setIsNewPlan(false);
                        setDirty(false);
                        setActiveTab('general');
                      }}
                      className={`w-full p-4 rounded-xl border transition-all text-left ${
                        isSelected
                          ? 'border-brand-500/50 bg-brand-500/5 shadow-md ring-1 ring-brand-500/20'
                          : 'border-overlay/[0.06] bg-surface-100 hover:bg-surface-100/80'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${plan.accent_color}20` }}>
                          <Icon size={14} style={{ color: plan.accent_color || '#64748b' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-surface-900 truncate">{plan.name}</div>
                          <div className="text-[10px] text-surface-400 uppercase tracking-wider">{plan.plan_key}</div>
                        </div>
                      </div>
                      <div className="text-lg font-bold text-surface-900">
                        ${plan.monthly_price}<span className="text-xs font-normal text-surface-400">/mo</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {plan.is_default && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded badge-blue border border-blue-800/40">Default</span>
                        )}
                        {plan.is_popular && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded badge-amber border border-amber-800/40">★ Popular</span>
                        )}
                        {subs > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-200 text-surface-500">{subs} users</span>
                        )}
                        {/* Stripe sync badge */}
                        {(() => {
                          const status = getStripeSyncStatus(plan);
                          if (status === 'synced') return (
                            <span className="text-[10px] px-1.5 py-0.5 rounded badge-emerald border border-emerald-800/40 flex items-center gap-0.5">
                              <Check size={8} /> Stripe
                            </span>
                          );
                          if (status === 'needs_sync') return (
                            <ContextualHelp helpKey="stripe-sync" iconSize={10}>
                              <span className="text-[10px] px-1.5 py-0.5 rounded badge-amber border border-amber-800/40 flex items-center gap-0.5">
                                <AlertTriangle size={8} /> Sync
                              </span>
                            </ContextualHelp>
                          );
                          return null; // free plan — no badge
                        })()}
                      </div>
                    </button>

                    {/* Quick-action overlay on hover — non-default plans only */}
                    {!plan.is_default && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(plan.id, false);
                          }}
                          title="Archive plan"
                          className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-amber-600 dark:text-amber-400 hover:bg-surface-900/80 transition-colors"
                        >
                          <Archive size={12} />
                        </button>
                        {deleteConfirm === plan.id ? (
                          <div className="flex gap-0.5">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDelete(plan.id); }}
                              className="p-1.5 rounded-lg bg-red-600 text-white text-[10px] font-medium px-2"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                              className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-surface-300 hover:bg-surface-900/80"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirm(plan.id);
                            }}
                            title="Delete plan"
                            className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-red-600 dark:text-red-400 hover:bg-surface-900/80 transition-colors"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* ── Add-Plan card ──────────────────────────────── */}
              {isNewPlan ? (
                <div className="flex-shrink-0 w-[180px] p-4 rounded-xl border border-brand-500/50 bg-brand-500/5 shadow-md ring-1 ring-brand-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-brand-500/10">
                      <Plus size={14} className="text-brand-500" />
                    </div>
                    <div className="text-sm font-semibold text-brand-600 dark:text-brand-400">New Plan</div>
                  </div>
                  <div className="text-xs text-surface-400">Configure below</div>
                </div>
              ) : !canAddPlan ? (
                <div className="flex-shrink-0 w-[180px] p-4 rounded-xl border-2 border-dashed border-surface-300 flex flex-col items-center justify-center gap-2 min-h-[120px] opacity-60 cursor-not-allowed">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-surface-100">
                    <Lock size={18} className="text-surface-400" />
                  </div>
                  <span className="text-xs font-medium text-surface-400 text-center">
                    Plan limit reached ({totalPlanCount}/{maxTotal})
                  </span>
                </div>
              ) : (
                <button
                  onClick={handleNewPlan}
                  className="flex-shrink-0 w-[180px] p-4 rounded-xl border-2 border-dashed border-surface-300 hover:border-brand-500/50 hover:bg-brand-500/5 transition-all group flex flex-col items-center justify-center gap-2 min-h-[120px]"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-surface-100 group-hover:bg-brand-500/10 transition-colors">
                    <Plus size={20} className="text-surface-400 group-hover:text-brand-500 transition-colors" />
                  </div>
                  <span className="text-sm font-medium text-surface-400 group-hover:text-brand-600 dark:text-brand-400 transition-colors">
                    Add Plan
                  </span>
                </button>
              )}
            </div>

            {/* ── Archived Plans Section ─────────────────────────── */}
            {archivedCount > 0 && (
              <div>
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className="flex items-center gap-2 text-sm text-surface-500 hover:text-surface-700 transition-colors group"
                >
                  <Archive size={14} />
                  <span>Archived Plans ({archivedCount})</span>
                  <svg className={`w-3.5 h-3.5 transition-transform ${showArchived ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showArchived && (
                  <div className="flex gap-3 overflow-x-auto pb-2 mt-3">
                    {archivedPlans.map(plan => {
                      const Icon = ICON_MAP[plan.icon_key || ''] || Sparkles;
                      const isSelected = plan.id === selectedPlanId && !isNewPlan;
                      const subs = subscriberCounts[plan.id] ?? 0;

                      return (
                        <div key={plan.id} className="flex-shrink-0 w-[180px] relative group">
                          <button
                            onClick={() => {
                              if (dirty && !confirm('Discard unsaved changes?')) return;
                              setSelectedPlanId(plan.id);
                              buildFormState(plan, features, planFeatures);
                              setIsNewPlan(false);
                              setDirty(false);
                              setActiveTab('general');
                            }}
                            className={`w-full p-4 rounded-xl border transition-all text-left opacity-70 ${
                              isSelected
                                ? 'border-brand-500/50 bg-brand-500/5 shadow-md ring-1 ring-brand-500/20 opacity-100'
                                : 'border-overlay/[0.06] bg-surface-100/50 hover:bg-surface-100/70 border-dashed'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-surface-200/50">
                                <Icon size={14} className="text-surface-400" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-surface-500 truncate">{plan.name}</div>
                                <div className="text-[10px] text-surface-400 uppercase tracking-wider">{plan.plan_key}</div>
                              </div>
                            </div>
                            <div className="text-lg font-bold text-surface-500">
                              ${plan.monthly_price}<span className="text-xs font-normal text-surface-400">/mo</span>
                            </div>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-200 text-surface-400 border border-surface-300">Archived</span>
                              {subs > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded badge-amber border border-amber-800/40">{subs} users</span>
                              )}
                              {/* Stripe sync badge */}
                              {(() => {
                                const status = getStripeSyncStatus(plan);
                                if (status === 'synced') return (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded badge-emerald border border-emerald-800/40 flex items-center gap-0.5">
                                    <Check size={8} /> Stripe
                                  </span>
                                );
                                if (status === 'needs_sync') return (
                                  <ContextualHelp helpKey="stripe-sync">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded badge-amber border border-amber-800/40 flex items-center gap-0.5">
                                      <AlertTriangle size={8} /> Sync
                                    </span>
                                  </ContextualHelp>
                                );
                                return null;
                              })()}
                            </div>
                          </button>

                          {/* Quick-action overlay on hover */}
                          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggle(plan.id, true);
                              }}
                              title="Restore plan"
                              className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-emerald-600 dark:text-emerald-400 hover:bg-surface-900/80 transition-colors"
                            >
                              <RotateCcw size={12} />
                            </button>
                            {deleteConfirm === plan.id ? (
                              <div className="flex gap-0.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDelete(plan.id); }}
                                  className="p-1.5 rounded-lg bg-red-600 text-white text-[10px] font-medium px-2"
                                >
                                  Confirm
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setDeleteConfirm(null); }}
                                  className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-surface-300 hover:bg-surface-900/80"
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteConfirm(plan.id);
                                }}
                                title="Delete permanently"
                                className="p-1.5 rounded-lg bg-surface-900/60 backdrop-blur text-red-600 dark:text-red-400 hover:bg-surface-900/80 transition-colors"
                              >
                                <Trash2 size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* ── Plan Editor ───────────────────────────────────────────── */}
      {formState && (
        <div className="glass-panel">
          {/* Editor header */}
          <div className="flex items-center justify-between p-4 border-b border-overlay/[0.06]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${formState.accent_color}20` }}>
                <PlanIcon size={18} style={{ color: formState.accent_color }} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-surface-900">
                  {isNewPlan ? 'New Plan' : formState.name}
                </h3>
                <span className="text-xs text-surface-400">{formState.plan_key || 'plan-key'}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isNewPlan && formState.id && !formState.is_default && (
                <>
                  <button
                    onClick={() => handleToggle(formState.id!, !formState.is_active)}
                    className="btn-secondary text-xs"
                    title={formState.is_active ? 'Archive this plan' : 'Restore this plan'}
                  >
                    {formState.is_active ? <><Archive size={12} /> Archive</> : <><RotateCcw size={12} /> Restore</>}
                  </button>
                  {deleteConfirm === formState.id ? (
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleDelete(formState.id!)} className="btn-primary text-xs bg-red-600 hover:bg-red-700">Confirm Delete</button>
                      <button onClick={() => setDeleteConfirm(null)} className="btn-secondary text-xs">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(formState.id!)} className="btn-secondary text-xs text-red-600 dark:text-red-400 hover:text-red-300">
                      <Trash2 size={12} /> Delete
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* ── Stripe Sync Status ─────────────────────────────────── */}
          {!isNewPlan && selectedPlan && Number(selectedPlan.monthly_price) > 0 && (
            <div className="px-4 py-3 border-b border-overlay/[0.06] bg-surface-50/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CloudLightning size={14} className="text-surface-500" />
                  <div>
                    {getStripeSyncStatus(selectedPlan) === 'synced' ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
                          <Check size={10} /> Stripe synced
                        </span>
                        <span className="text-[10px] text-surface-400 font-mono">
                          {selectedPlan.stripe_product_id?.slice(0, 20)}...
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs font-medium text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertTriangle size={10} /> Needs Stripe sync — checkout won't work until synced
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Read-only Stripe IDs (collapsible for debugging) */}
                  {getStripeSyncStatus(selectedPlan) === 'synced' && (
                    <details className="text-[10px] text-surface-400">
                      <summary className="cursor-pointer hover:text-surface-600 select-none">IDs</summary>
                      <div className="mt-1 space-y-0.5 font-mono">
                        <div>Product: {selectedPlan.stripe_product_id}</div>
                        <div>Monthly: {selectedPlan.stripe_monthly_price_id}</div>
                        <div>Annual: {selectedPlan.stripe_annual_price_id}</div>
                      </div>
                    </details>
                  )}
                  {getStripeSyncStatus(selectedPlan) === 'needs_sync' && (
                    <button
                      onClick={() => handleSyncStripe(selectedPlan.id)}
                      disabled={syncingPlanId === selectedPlan.id}
                      className="btn-primary text-xs"
                    >
                      {syncingPlanId === selectedPlan.id
                        ? <><RefreshCw size={10} className="animate-spin" /> Syncing...</>
                        : <><CloudLightning size={10} /> Sync to Stripe</>}
                    </button>
                  )}
                  {getStripeSyncStatus(selectedPlan) === 'synced' && (
                    <button
                      onClick={() => handleSyncStripe(selectedPlan.id)}
                      disabled={syncingPlanId === selectedPlan.id}
                      className="btn-secondary text-xs"
                      title="Force re-sync Stripe prices"
                    >
                      {syncingPlanId === selectedPlan.id
                        ? <><RefreshCw size={10} className="animate-spin" /> Syncing...</>
                        : <><RefreshCw size={10} /> Re-sync</>}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Tabs. Per-tab count badge: only the Features tab carries
              one (enabled / total across every category), since
              General + Pricing are forms, not on/off lists. The
              count tracks formState directly so it updates live as
              the admin toggles features on/off in the panel below. */}
          {(() => {
            // Pull these once; reused for the per-category breakdowns
            // further down so we don't recompute on every render of
            // the section headings.
            const totalAllFeatures = features.length;
            const totalEnabled = features.reduce(
              (n, f) => n + (formState.features[f.id]?.is_enabled ? 1 : 0),
              0,
            );
            return (
              <div className="flex border-b border-overlay/[0.06]">
                {(['general', 'pricing', 'features'] as Tab[]).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-5 py-3 text-sm font-medium transition-colors border-b-2 inline-flex items-center gap-2 ${
                      activeTab === tab
                        ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                        : 'border-transparent text-surface-500 hover:text-surface-700'
                    }`}
                  >
                    <span>{tab.charAt(0).toUpperCase() + tab.slice(1)}</span>
                    {tab === 'features' && totalAllFeatures > 0 && (
                      <span className={`text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full border ${
                        activeTab === tab
                          ? 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border-brand-500/30'
                          : 'bg-surface-100 text-surface-500 border-surface-200'
                      }`}>
                        {totalEnabled}/{totalAllFeatures}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            );
          })()}

          <div className="p-5">
            {/* ── General Tab ──────────────────────────────────────── */}
            {activeTab === 'general' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="space-y-4">
                  <div>
                    <label className="input-label">Plan Name</label>
                    <input type="text" value={formState.name} onChange={e => updateForm('name', e.target.value)} className="input-field text-sm" placeholder="e.g. Starter, Plus, VIP" />
                  </div>
                  <div>
                    <label className="input-label">Plan Key (slug)</label>
                    <input type="text" value={formState.plan_key} onChange={e => updateForm('plan_key', e.target.value)} className="input-field text-sm font-mono" placeholder="e.g. starter" disabled={!isNewPlan} />
                    {isNewPlan && <p className="text-[10px] text-surface-400 mt-0.5">Auto-generated from name. Must be unique.</p>}
                  </div>
                  <div>
                    <label className="input-label">Description</label>
                    <textarea value={formState.description} onChange={e => updateForm('description', e.target.value)} className="input-field text-sm" rows={2} placeholder="Brief plan description" />
                  </div>
                  <div>
                    <label className="input-label">Tagline</label>
                    <input type="text" value={formState.tagline} onChange={e => updateForm('tagline', e.target.value)} className="input-field text-sm" placeholder="Short tagline for pricing card" />
                  </div>
                  <div>
                    <label className="input-label">CTA Button Label</label>
                    <input type="text" value={formState.cta_label} onChange={e => updateForm('cta_label', e.target.value)} className="input-field text-sm" placeholder="Start Free Trial" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="input-label">Accent Color</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {COLOR_PRESETS.map(c => (
                        <button
                          key={c}
                          onClick={() => updateForm('accent_color', c)}
                          className={`w-7 h-7 rounded-lg border-2 transition-all ${formState.accent_color === c ? 'border-white scale-110 shadow-lg' : 'border-transparent hover:scale-105'}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                      <input type="color" value={formState.accent_color} onChange={e => updateForm('accent_color', e.target.value)} className="w-7 h-7 rounded-lg cursor-pointer border-0 p-0" />
                    </div>
                  </div>

                  <div>
                    <label className="input-label">Icon</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {B2C_ICON_OPTIONS.map(key => {
                        const Ic = ICON_MAP[key] || Sparkles;
                        return (
                          <button
                            key={key}
                            onClick={() => updateForm('icon_key', key)}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                              formState.icon_key === key
                                ? 'bg-brand-500/10 ring-1 ring-brand-500/40 text-brand-600 dark:text-brand-400'
                                : 'bg-surface-100 text-surface-500 hover:bg-surface-200'
                            }`}
                            title={key}
                          >
                            <Ic size={16} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={formState.is_popular} onChange={e => updateForm('is_popular', e.target.checked)} className="w-4 h-4 rounded border-surface-300 text-brand-500 focus:ring-brand-500/40" />
                      <span className="text-sm text-surface-700">Mark as Popular</span>
                    </label>
                  </div>

                  <div>
                    <label className="input-label">Sort Order</label>
                    <input type="number" value={formState.sort_order} onChange={e => updateForm('sort_order', e.target.value)} className="input-field text-sm w-24" />
                  </div>
                </div>
              </div>
            )}

            {/* ── Pricing Tab ─────────────────────────────────────── */}
            {activeTab === 'pricing' && (
              <div className="space-y-5">
                {/* Free plan limit warning */}
                {isNewPlan && atFreeLimit && (
                  <div className="flex items-center gap-2 p-3 rounded-xl banner-amber border text-sm text-amber-600 dark:text-amber-400">
                    <AlertTriangle size={14} />
                    <span>Free plan limit reached ({freePlanCount}/{maxFree}) — set a price above $0.</span>
                  </div>
                )}
                {(() => {
                  const currentMonthly = parseFloat(formState.monthly_price) || 0;
                  const isFreePricing = currentMonthly === 0 && !isNewPlan && formState.plan_key === 'free';
                  return isFreePricing ? (
                    <div className="p-4 rounded-xl bg-surface-50 border border-surface-200">
                      <div className="text-2xl font-bold text-surface-900">$0</div>
                      <p className="text-xs text-surface-500 mt-1">Free plan — no revenue share applies</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="input-label">Monthly Price</label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">$</span>
                            <input type="number" step="0.01" min="0" value={formState.monthly_price} onChange={e => updateForm('monthly_price', e.target.value)} className="input-field pl-7 text-sm" placeholder="0 = free plan" />
                          </div>
                          {currentMonthly === 0 && <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">$0 = Free plan (no revenue share)</p>}
                        </div>
                      <div>
                        <label className="input-label">Annual Price</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">$</span>
                          <input type="number" step="0.01" min="0" value={formState.annual_price} onChange={e => updateForm('annual_price', e.target.value)} className="input-field pl-7 text-sm" />
                        </div>
                      </div>
                    </div>

                    {/* Revenue share */}
                    <div className="p-4 rounded-xl bg-surface-50 border border-surface-200">
                      <div className="flex items-center gap-2 mb-3">
                        <Percent size={14} className="text-surface-500" />
                        <span className="text-xs font-semibold text-surface-700 uppercase tracking-wider">Revenue Share</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-surface-500">DiscoveryGem Share</label>
                          <div className="relative">
                            <input type="number" step="1" min="20" max="100" value={formState.unravel_share_pct} onChange={e => {
                              const val = e.target.value;
                              setFormState(prev => prev ? { ...prev, unravel_share_pct: val, tenant_share_pct: String(100 - (parseFloat(val) || 0)) } : prev);
                              setDirty(true); setSaveSuccess(false); setSaveError(null);
                            }} className="input-field pr-8 text-sm font-medium" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-surface-400">%</span>
                          </div>
                          <p className="text-[10px] text-surface-400 mt-0.5">Min 20%</p>
                        </div>
                        <div>
                          <label className="text-xs text-surface-500">Tenant Share</label>
                          <div className="flex items-center h-[38px] px-3 rounded-lg bg-surface-100 border border-surface-200">
                            <span className="text-sm font-medium text-surface-700">{Math.max(0, 100 - (parseFloat(formState.unravel_share_pct) || 0))}%</span>
                          </div>
                          <p className="text-[10px] text-surface-400 mt-0.5">Auto-calculated</p>
                        </div>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-surface-200 overflow-hidden flex">
                        <div className="bg-brand-500 rounded-l-full transition-all" style={{ width: `${parseFloat(formState.unravel_share_pct) || 30}%` }} />
                        <div className="bg-teal-400 rounded-r-full transition-all" style={{ width: `${100 - (parseFloat(formState.unravel_share_pct) || 30)}%` }} />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-brand-600 dark:text-brand-400">DiscoveryGem</span>
                        <span className="text-[10px] text-teal-400">Tenant</span>
                      </div>
                    </div>
                  </>
                  );
                })()}
              </div>
            )}

            {/* ── Features Tab ─────────────────────────────────────── */}
            {activeTab === 'features' && (
              <div className="space-y-4">
                <p className="text-xs text-surface-500">
                  Toggle features on/off for this plan. Features locked by your B2B plan are greyed out.
                </p>

                {/* Group by category. Stored category values are 'core' |
                    'ai' | 'budget' | 'collaboration' (set by App DB migrations);
                    the displayed labels follow the FEATURE_META convention used
                    by the super-admin Platform Plans tab so tenant admins see
                    the same category names. 'ai' → "Smart Features" because the
                    bucket holds Discovery Gem, Voice Interpreter, Packing List,
                    Place Recommendations, etc. — not just AI chatbots. */}
                {(([
                  { key: 'core',          label: 'Core' },
                  { key: 'ai',            label: 'Smart Features' },
                  { key: 'budget',        label: 'Budget' },
                  { key: 'collaboration', label: 'Collaboration' },
                ] as const)).map(({ key: cat, label }) => {
                  const catFeatures = features.filter(f => f.category === cat).sort((a, b) => a.name.localeCompare(b.name));
                  if (catFeatures.length === 0) return null;
                  // Per-category enabled count — surfaces a "5/9" badge
                  // on the section heading so the admin can scan
                  // category coverage without counting toggles manually.
                  // Mirrors the overall count badge on the Features tab.
                  const catEnabled = catFeatures.reduce(
                    (n, f) => n + (formState.features[f.id]?.is_enabled ? 1 : 0),
                    0,
                  );

                  return (
                    <div key={cat}>
                      <h4 className="flex items-center gap-2 text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2 mt-4">
                        <span>{label}</span>
                        <span className="text-[10px] font-semibold tabular-nums px-1.5 py-0.5 rounded-full bg-surface-100 text-surface-500 border border-surface-200 normal-case tracking-normal">
                          {catEnabled}/{catFeatures.length}
                        </span>
                      </h4>
                      <div className="space-y-2">
                        {catFeatures.map(f => {
                          const fState = formState.features[f.id];
                          if (!fState) return null;
                          const allowed = allowedB2CKeys.has(f.key);
                          const isCount = f.limit_type === 'count' || f.limit_type === 'count_per_trip';

                          return (
                            <div key={f.id} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                              !allowed
                                ? 'bg-surface-50/50 border-surface-200/50 opacity-60'
                                : fState.is_enabled
                                  ? 'bg-emerald-900/10 border-emerald-800/30'
                                  : 'bg-surface-50 border-surface-200'
                            }`}>
                              {/* Toggle */}
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={fState.is_enabled}
                                  disabled={!allowed}
                                  onChange={e => updateFeature(f.id, 'is_enabled', e.target.checked)}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-surface-300 peer-focus:outline-none rounded-full peer peer-checked:bg-emerald-500 peer-disabled:bg-surface-200 transition-colors">
                                  <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${fState.is_enabled ? 'translate-x-4' : ''}`} />
                                </div>
                              </label>

                              {/* Feature info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-surface-900">{f.name}</span>
                                  {!allowed && (
                                    <span className="inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                                      <Lock size={10} /> Locked
                                    </span>
                                  )}
                                  {allowed && isCount && (() => {
                                    const ceil = ceilingLimits[f.key];
                                    return (
                                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-500 border border-surface-200">
                                        Cap: {ceil != null && ceil > 0 ? ceil : '∞'}
                                      </span>
                                    );
                                  })()}
                                  {f.is_beta && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded badge-purple border border-purple-800/40">Beta</span>
                                  )}
                                </div>
                                <div className="text-xs text-surface-400">{f.description}</div>
                              </div>

                              {/* Limit inputs for count features */}
                              {isCount && fState.is_enabled && allowed && (() => {
                                const ceil = ceilingLimits[f.key];        // null = unlimited
                                const hasCeil = ceil != null && ceil > 0;
                                return (
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <div className="relative">
                                    <input
                                      type="number"
                                      min="0"
                                      max={hasCeil ? ceil : undefined}
                                      value={fState.limit_value}
                                      onChange={e => {
                                        let v = e.target.value;
                                        if (hasCeil && v !== '' && parseInt(v) > ceil) v = String(ceil);
                                        updateFeature(f.id, 'limit_value', v);
                                      }}
                                      className="input-field text-xs w-20"
                                      placeholder="∞"
                                    />
                                    {hasCeil && (
                                      <span className="absolute -bottom-4 left-0 text-[9px] text-amber-600 dark:text-amber-400 whitespace-nowrap">
                                        max {ceil}
                                      </span>
                                    )}
                                  </div>
                                  <select
                                    value={fState.limit_period}
                                    onChange={e => updateFeature(f.id, 'limit_period', e.target.value)}
                                    className="input-field text-xs w-24"
                                  >
                                    <option value="">Per trip</option>
                                    <option value="daily">Daily</option>
                                    <option value="monthly">Monthly</option>
                                    <option value="total">Total</option>
                                  </select>
                                  <input
                                    type="text"
                                    value={fState.display_limit}
                                    onChange={e => updateFeature(f.id, 'display_limit', e.target.value)}
                                    className="input-field text-xs w-32"
                                    placeholder="Display text"
                                  />
                                </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Revenue Preview ────────────────────────────────────────── */}
      <div className="glass-panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <Calculator size={16} className="text-surface-500" />
          <h3 className="text-sm font-semibold text-surface-900">Revenue Preview</h3>
          <span className="text-xs text-surface-400 ml-1">Estimate monthly revenue</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {plans.filter(p => p.monthly_price > 0 && p.is_active).map(plan => {
            // Use formState price/name for the plan being edited
            const isEditing = formState && plan.plan_key === formState.plan_key;
            const displayName = isEditing ? (formState.name || plan.name) : plan.name;
            const price = isEditing ? (parseFloat(formState.monthly_price) || 0) : plan.monthly_price;
            if (price === 0) return null;
            return (
              <div key={plan.plan_key}>
                <label className="text-xs text-surface-500">{displayName} users</label>
                <input
                  type="number" min="0"
                  value={previewUsers[plan.plan_key] || '0'}
                  onChange={e => setPreviewUsers(prev => ({ ...prev, [plan.plan_key]: e.target.value }))}
                  className="input-field text-sm"
                />
              </div>
            );
          })}
          {/* Include a new unsaved paid plan in the preview inputs */}
          {formState && isNewPlan && (parseFloat(formState.monthly_price) || 0) > 0 && (
            <div>
              <label className="text-xs text-surface-500">{formState.name || 'New Plan'} users</label>
              <input
                type="number" min="0"
                value={previewUsers[formState.plan_key] || '0'}
                onChange={e => setPreviewUsers(prev => ({ ...prev, [formState.plan_key]: e.target.value }))}
                className="input-field text-sm"
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-3 rounded-xl bg-surface-50 border border-surface-200 text-center">
            <div className="text-xs text-surface-500 mb-1">Gross Monthly</div>
            <div className="text-lg font-bold text-surface-900">${revenue.total.toFixed(2)}</div>
            <div className="text-xs text-surface-400">${(revenue.total * 12).toFixed(2)}/yr</div>
          </div>
          <div className="p-3 rounded-xl bg-brand-500/10 border border-brand-500/20 text-center">
            <div className="text-xs text-brand-600 dark:text-brand-400 mb-1">DiscoveryGem Monthly</div>
            <div className="text-lg font-bold text-brand-600 dark:text-brand-400">${revenue.unravelMonthly.toFixed(2)}</div>
            <div className="text-xs text-brand-600 dark:text-brand-400">${(revenue.unravelMonthly * 12).toFixed(2)}/yr</div>
          </div>
          <div className="p-3 rounded-xl banner-teal border text-center">
            <div className="text-xs text-teal-400 mb-1">Tenant Monthly</div>
            <div className="text-lg font-bold text-teal-400">${revenue.tenantMonthly.toFixed(2)}</div>
            <div className="text-xs text-teal-400">${(revenue.tenantMonthly * 12).toFixed(2)}/yr</div>
          </div>
        </div>
      </div>

      {/* ── Save Bar ──────────────────────────────────────────────── */}
      {(dirty || saveSuccess || saveError) && (
        <div className="flex items-center justify-between p-4 glass-panel sticky bottom-4">
          <div className="flex-1">
            {saveError && <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400"><AlertTriangle size={14} /> {saveError}</div>}
            {saveSuccess && !dirty && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                <Check size={14} /> Plan saved successfully
                <button onClick={() => setSaveSuccess(false)} className="ml-2 p-0.5 rounded hover:bg-emerald-50 dark:bg-emerald-900/20"><X size={12} /></button>
              </div>
            )}
          </div>
          {dirty && (
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm disabled:opacity-50">
              {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> {isNewPlan ? 'Create Plan' : 'Save Changes'}</>}
            </button>
          )}
        </div>
      )}

      {/* ── Pricing Assistant modal ──────────────────────────────────
          Mounted at the page root; opens against the currently-
          selected plan. Endpoint points at the B2C variant edge fn
          (pricing-assistant-b2c) which uses an anonymous internal
          benchmark instead of external competitor prices, and is
          forbidden by its system prompt from naming any other
          tenant. extraBody passes current_tenant_id so the edge fn
          can exclude this tenant from the benchmark aggregate. */}
      {assistantOpen && (
        <PricingAssistantPanel
          context={assistantContext}
          messages={assistantContext ? (conversations[assistantContext.id] ?? []) : []}
          onMessagesChange={handleConversationChange}
          onReset={handleConversationReset}
          onApply={handleAssistantApply}
          onClose={() => setAssistantOpen(false)}
          endpoint="pricing-assistant-b2c"
          extraBody={{ current_tenant_id: tenant.id }}
          taglineText="anonymous benchmark across DiscoveryGem partners"
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Kickoff message builder for the Pricing Assistant — B2C variant.
// Visible to the tenant admin as the first user turn so the agent has
// full context. Framed for a consumer plan (no seat/storage limits —
// those are tenant-level, not per-B2C-plan), and the closing line
// explicitly asks for the anonymous internal benchmark rather than
// external competitor data.
// ─────────────────────────────────────────────────────────────────────
function buildB2CKickoff(args: {
  display_name:   string;
  monthly_usd:    number;
  annual_usd:     number;
  feature_labels: string[];
}): string {
  const priceLine = args.monthly_usd === 0 && args.annual_usd === 0
    ? 'Currently priced as **Free**.'
    : `Currently priced at **$${args.monthly_usd.toFixed(2)}/mo, $${args.annual_usd.toFixed(0)}/yr**.`;

  const featureList = args.feature_labels.length > 0
    ? args.feature_labels.map(f => `- ${f}`).join('\n')
    : '_(no features currently enabled)_';

  return [
    `I'm pricing the **${args.display_name}** plan for my travellers.`,
    '',
    priceLine,
    `Enabled feature count: ${args.feature_labels.length}.`,
    '',
    'Enabled features:',
    featureList,
    '',
    'Suggest a price for this plan and compare it to the anonymous benchmark across other DiscoveryGem partners. Explain why this price is right based on the internal benchmark and my feature mix. Do NOT mention any specific tenant or any external competitor.',
  ].join('\n');
}
